import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Workflow, CheckCircle2, Clock, User, AlertCircle,
  PlayCircle, Circle, Check, X, Send, UserPlus, MessageSquare,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatDateTime } from '@/lib/api'
import { CamposForm } from './BPM'

export default function BPMInstanciaPage() {
  const { id } = useParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [dadosTarefa, setDadosTarefa] = useState<Record<string, any>>({})
  const [comentario, setComentario] = useState('')
  const [novoComentario, setNovoComentario] = useState('')
  const [showTransferir, setShowTransferir] = useState<number | null>(null)
  const [showCancelar, setShowCancelar] = useState(false)

  const { data: inst, isLoading } = useQuery<any>({
    queryKey: ['bpm-instancia', id],
    queryFn: async () => (await api.get(`/bpm/instancias/${id}`)).data,
    enabled: !!id,
  })

  const { data: processo } = useQuery<any>({
    queryKey: ['bpm-processo', inst?.processo_id],
    queryFn: async () => (await api.get(`/bpm/processos/${inst.processo_id}`)).data,
    enabled: !!inst?.processo_id,
  })

  const { data: usuarios = [] } = useQuery<any[]>({
    queryKey: ['usuarios-bpm'],
    queryFn: async () => (await api.get('/auth/usuarios').catch(() => ({ data: [] }))).data || [],
  })

  function invalidar() {
    qc.invalidateQueries({ queryKey: ['bpm-instancia', id] })
    qc.invalidateQueries({ queryKey: ['bpm-minhas-tarefas'] })
    qc.invalidateQueries({ queryKey: ['bpm-dashboard'] })
  }

  const concluir = useMutation({
    mutationFn: async (tarefaId: number) => (await api.post(`/bpm/tarefas/${tarefaId}/concluir`, {
      dados: dadosTarefa, comentario: comentario || undefined,
    })).data,
    onSuccess: () => {
      toast.success('Tarefa concluída')
      setDadosTarefa({})
      setComentario('')
      invalidar()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const cancelar = useMutation({
    mutationFn: async (motivo: string) => (await api.post(`/bpm/instancias/${id}/cancelar`, { motivo })).data,
    onSuccess: () => { toast.success('Instância cancelada'); setShowCancelar(false); invalidar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const transferir = useMutation({
    mutationFn: async ({ tarefaId, usuario_id, motivo }: any) =>
      (await api.post(`/bpm/tarefas/${tarefaId}/transferir`, { usuario_id, motivo })).data,
    onSuccess: () => { toast.success('Tarefa transferida'); setShowTransferir(null); invalidar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const comentar = useMutation({
    mutationFn: async (texto: string) => (await api.post(`/bpm/instancias/${id}/comentarios`, { texto })).data,
    onSuccess: () => { setNovoComentario(''); invalidar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  if (isLoading) return <div className="p-6">Carregando...</div>
  if (!inst) return <div className="p-6">Instância não encontrada</div>

  const tarefaAtiva = inst.tarefas?.find((t: any) => t.status === 'pendente')
  const nodesOrdenados = processo?.nodes?.slice().sort((a: any, b: any) => a.ordem - b.ordem) || []
  const vencido = inst.sla_vence_em && new Date(inst.sla_vence_em) < new Date() && inst.status === 'em_andamento'

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <button className="btn-ghost" onClick={() => nav('/bpm')}>
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Workflow className="w-6 h-6" /> {inst.titulo}
          </h1>
          <p className="text-sm text-ink-500">
            {inst.processo_nome} · #{inst.numero} · aberto {formatDateTime(inst.iniciado_em)}
          </p>
        </div>
        <StatusBadge status={inst.status} vencido={vencido} />
        {inst.status === 'em_andamento' && (
          <button onClick={() => setShowCancelar(true)} className="btn-ghost text-rose-600">
            <X className="w-4 h-4" /> Cancelar
          </button>
        )}
      </div>

      {/* Pipeline visual simples */}
      <div className="card card-body mb-4 overflow-x-auto">
        <div className="flex items-center gap-0 min-w-max">
          {nodesOrdenados.map((n: any, i: number) => {
            const passou = inst.historico?.some(
              (h: any) => h.tipo === 'tarefa_concluida' && h.node_codigo === n.codigo,
            ) || (n.tipo === 'fim' && inst.status === 'concluido')
            const atual = inst.node_atual_codigo === n.codigo && inst.status === 'em_andamento'
            const cor = passou ? 'bg-emerald-500 text-white border-emerald-500'
              : atual ? 'bg-brand-500 text-white border-brand-500 ring-4 ring-brand-100'
              : 'bg-ink-100 text-ink-400 border-ink-200'
            const Ic = passou ? Check : atual ? PlayCircle : Circle
            return (
              <div key={n.id} className="flex items-center">
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${cor}`}>
                    <Ic className="w-5 h-5" />
                  </div>
                  <div className={`text-[11px] max-w-[100px] text-center ${atual ? 'font-semibold' : 'text-ink-500'}`}>
                    {n.nome}
                  </div>
                </div>
                {i < nodesOrdenados.length - 1 && (
                  <div className={`h-0.5 w-10 md:w-16 mx-1 mt-[-16px] ${passou ? 'bg-emerald-400' : 'bg-ink-200'}`} />
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Tarefa ativa */}
          {tarefaAtiva && inst.status === 'em_andamento' && (
            <div className="card border-brand-300">
              <div className="border-b border-ink-200 px-4 py-3 bg-brand-50 flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-brand-600" />
                <span className="font-semibold">Tarefa atual: {tarefaAtiva.node_nome}</span>
                {tarefaAtiva.prazo && (
                  <span className="text-xs text-ink-500 ml-auto flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Até {formatDateTime(tarefaAtiva.prazo)}
                  </span>
                )}
              </div>
              <div className="p-4 space-y-3">
                <CamposForm
                  campos={processo?.nodes?.find((n: any) => n.codigo === tarefaAtiva.node_codigo)?.form_schema?.campos || []}
                  valores={dadosTarefa}
                  setValores={setDadosTarefa}
                />
                <div>
                  <label className="label">Comentário <span className="text-ink-400">(opcional)</span></label>
                  <textarea className="input min-h-[60px]" value={comentario}
                    onChange={e => setComentario(e.target.value)} />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => concluir.mutate(tarefaAtiva.id)} disabled={concluir.isPending}
                    className="btn-primary flex-1">
                    <CheckCircle2 className="w-4 h-4" />
                    {concluir.isPending ? 'Concluindo...' : 'Concluir tarefa'}
                  </button>
                  <button onClick={() => setShowTransferir(tarefaAtiva.id)} className="btn-secondary">
                    <UserPlus className="w-4 h-4" /> Transferir
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Dados do processo */}
          {inst.dados && Object.keys(inst.dados).length > 0 && (
            <div className="card">
              <div className="px-4 py-3 border-b border-ink-200 font-semibold">Dados coletados</div>
              <div className="p-4 grid grid-cols-2 gap-3 text-sm">
                {Object.entries(inst.dados).map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[10px] uppercase text-ink-400">{k}</div>
                    <div>{String(v ?? '-')}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Histórico + comentários */}
          <div className="card">
            <div className="px-4 py-3 border-b border-ink-200 font-semibold flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Histórico & comentários
            </div>
            <div className="divide-y divide-ink-100">
              {inst.historico?.map((h: any) => (
                <div key={h.id} className="p-3 text-sm flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-ink-100 flex items-center justify-center shrink-0">
                    {h.tipo === 'iniciado' && <PlayCircle className="w-3 h-3" />}
                    {h.tipo === 'tarefa_criada' && <Clock className="w-3 h-3" />}
                    {h.tipo === 'tarefa_concluida' && <Check className="w-3 h-3 text-emerald-600" />}
                    {h.tipo === 'concluido' && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                    {h.tipo === 'cancelado' && <X className="w-3 h-3 text-rose-600" />}
                    {h.tipo === 'tarefa_transferida' && <UserPlus className="w-3 h-3 text-amber-600" />}
                    {h.tipo === 'comentario' && <MessageSquare className="w-3 h-3 text-brand-600" />}
                  </div>
                  <div className="flex-1">
                    <div>
                      <span className="font-medium">{tipoLabel(h.tipo)}</span>
                      {h.node_codigo && <span className="text-ink-500 ml-1">— {h.node_codigo}</span>}
                    </div>
                    <div className="text-xs text-ink-500">
                      {h.usuario_email || 'sistema'} · {formatDateTime(h.criado_em)}
                    </div>
                    {h.detalhes?.comentario && (
                      <div className="text-xs text-ink-600 mt-1 italic">"{h.detalhes.comentario}"</div>
                    )}
                    {h.tipo === 'comentario' && h.detalhes?.texto && (
                      <div className="text-sm text-ink-700 mt-1 bg-ink-50 p-2 rounded">{h.detalhes.texto}</div>
                    )}
                    {h.tipo === 'cancelado' && h.detalhes?.motivo && (
                      <div className="text-xs text-ink-600 mt-1 italic">Motivo: {h.detalhes.motivo}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-ink-200 bg-ink-50 flex gap-2">
              <input className="input flex-1" placeholder="Adicionar comentário..."
                value={novoComentario} onChange={e => setNovoComentario(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && novoComentario.trim() && comentar.mutate(novoComentario)} />
              <button onClick={() => novoComentario.trim() && comentar.mutate(novoComentario)}
                disabled={!novoComentario.trim() || comentar.isPending} className="btn-primary">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="card card-body text-sm space-y-2">
            <div>
              <div className="text-[10px] uppercase text-ink-400">Solicitante</div>
              <div className="flex items-center gap-1.5"><User className="w-3 h-3" /> {inst.solicitante_nome || '-'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-ink-400">Status</div>
              <div>{inst.status}</div>
            </div>
            {inst.sla_vence_em && (
              <div>
                <div className="text-[10px] uppercase text-ink-400">SLA</div>
                <div className={vencido ? 'text-rose-600 font-medium' : ''}>{formatDateTime(inst.sla_vence_em)}</div>
              </div>
            )}
            {inst.concluido_em && (
              <div>
                <div className="text-[10px] uppercase text-ink-400">Concluído em</div>
                <div>{formatDateTime(inst.concluido_em)}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCancelar && <ModalCancelar onClose={() => setShowCancelar(false)} onConfirm={(m) => cancelar.mutate(m)} />}
      {showTransferir !== null && (
        <ModalTransferir
          usuarios={usuarios}
          onClose={() => setShowTransferir(null)}
          onConfirm={(usuario_id, motivo) => transferir.mutate({ tarefaId: showTransferir, usuario_id, motivo })}
        />
      )}
    </div>
  )
}

function ModalCancelar({ onClose, onConfirm }: { onClose: () => void; onConfirm: (m: string) => void }) {
  const [motivo, setMotivo] = useState('')
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-ink-200 font-semibold flex items-center gap-2 text-rose-700">
          <X className="w-5 h-5" /> Cancelar processo
        </div>
        <div className="p-4 space-y-2">
          <p className="text-sm text-ink-600">Essa ação finaliza o processo e cancela todas as tarefas pendentes.</p>
          <textarea className="input min-h-[80px]" placeholder="Motivo (opcional)"
            value={motivo} onChange={e => setMotivo(e.target.value)} />
        </div>
        <div className="p-4 border-t border-ink-200 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Voltar</button>
          <button onClick={() => onConfirm(motivo)} className="btn-danger">Cancelar processo</button>
        </div>
      </div>
    </div>
  )
}

function ModalTransferir({ usuarios, onClose, onConfirm }: {
  usuarios: any[]; onClose: () => void; onConfirm: (uid: number, motivo: string) => void
}) {
  const [uid, setUid] = useState<number | null>(null)
  const [motivo, setMotivo] = useState('')
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-ink-200 font-semibold flex items-center gap-2">
          <UserPlus className="w-5 h-5" /> Transferir tarefa
        </div>
        <div className="p-4 space-y-2">
          <div>
            <label className="label">Transferir para</label>
            <select className="input" value={uid || ''} onChange={e => setUid(Number(e.target.value) || null)}>
              <option value="">Selecione...</option>
              {usuarios.map((u: any) => <option key={u.id} value={u.id}>{u.email}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Motivo (opcional)</label>
            <textarea className="input min-h-[60px]" value={motivo} onChange={e => setMotivo(e.target.value)} />
          </div>
        </div>
        <div className="p-4 border-t border-ink-200 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={() => uid && onConfirm(uid, motivo)} disabled={!uid} className="btn-primary">
            Transferir
          </button>
        </div>
      </div>
    </div>
  )
}

function StatusBadge({ status, vencido }: { status: string; vencido?: boolean }) {
  const map: Record<string, { label: string; cor: string; Ic: any }> = {
    em_andamento: { label: 'Em andamento', cor: 'bg-blue-100 text-blue-700', Ic: PlayCircle },
    concluido: { label: 'Concluído', cor: 'bg-emerald-100 text-emerald-700', Ic: CheckCircle2 },
    cancelado: { label: 'Cancelado', cor: 'bg-ink-100 text-ink-500', Ic: AlertCircle },
  }
  const m = map[status] || map.em_andamento
  const Ic = m.Ic
  const cor = vencido ? 'bg-rose-100 text-rose-700' : m.cor
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${cor}`}>
      <Ic className="w-3 h-3" />
      {vencido ? 'Atrasado' : m.label}
    </span>
  )
}

function tipoLabel(t: string) {
  return {
    iniciado: 'Processo iniciado',
    tarefa_criada: 'Tarefa criada',
    tarefa_concluida: 'Tarefa concluída',
    concluido: 'Processo concluído',
    cancelado: 'Processo cancelado',
    tarefa_transferida: 'Tarefa transferida',
    comentario: 'Comentário',
  }[t] || t
}
